<template>
  <v-dialog v-model="dialogVisible" max-width="600" scrollable>
    <v-card class="rounded-xl overflow-hidden" style="background-color: #2E7D32;">
      
      <div class="px-6 pt-6 pb-4">
        <div class="d-flex justify-space-between align-center mb-2">
          <span class="white--text text-h6 font-weight-bold">{{ nomeVale }}</span>
          <v-btn icon dark small @click="fecharDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        
        <v-expand-transition>
          <div class="text-center white--text pb-2" v-if="!carregando && movimentacoes.length > 0">
            <div class="subtitle-2 font-weight-regular" style="opacity: 0.85;">Saldo Atual</div>
            <div class="text-h3 font-weight-black mt-1" style="letter-spacing: -1px;">
              {{ formatarMoeda(saldoAtual) }}
            </div>
          </div>
        </v-expand-transition>
      </div>

      <v-card-text class="white pa-0" style="border-radius: 30px 30px 0 0; min-height: 400px;">
        
        <div v-if="carregando" class="d-flex justify-center align-center" style="height: 300px;">
          <v-progress-circular indeterminate color="#2E7D32" size="50"></v-progress-circular>
        </div>

        <div v-else-if="movimentacoes.length === 0" class="d-flex flex-column justify-center align-center text-center pa-10" style="height: 300px;">
          <v-icon size="64" color="grey lighten-2">mdi-receipt-text-outline</v-icon>
          <div class="mt-4 text-h6 grey--text text--darken-2 font-weight-bold">Nenhuma movimentação</div>
          <div class="caption grey--text">Não há registros para este cartão.</div>
        </div>

        <div v-else class="pt-6 pb-6 px-4">
          <template v-for="(mov, index) in movimentacoes">
            <div :key="index" class="d-flex align-start py-4 px-2" style="border-bottom: 1px solid #F5F5F5;">
              
              <div class="text-center mr-4 mt-1" style="width: 45px;">
                <div class="font-weight-black" style="color: #1B5E20; font-size: 16px; line-height: 1;">
                  {{ getDia(mov.emissao) }}
                </div>
                <div class="font-weight-bold text-uppercase mt-1" style="color: #9E9E9E; font-size: 10px;">
                  {{ getMes(mov.emissao) }}
                </div>
              </div>

              <v-icon 
                :color="mov.tipo_lanc === 'C' ? 'green darken-2' : 'grey darken-1'" 
                size="20" 
                class="mr-4 mt-1"
              >
                {{ mov.tipo_lanc === 'C' ? 'mdi-plus-circle-outline' : 'mdi-minus-circle-outline' }}
              </v-icon>

              <div class="flex-grow-1 mr-2" style="min-width: 0;">
                <div class="font-weight-bold text-truncate" style="color: #212121; font-size: 14px;">
                  {{ mov.operacao || 'Movimentação' }}
                </div>
                <div v-if="mov.descricao" class="text-truncate mt-1 font-weight-medium" style="color: #757575; font-size: 12px;">
                  {{ mov.descricao }}
                </div>
              </div>

              <div class="text-right">
                <div class="font-weight-black" :style="{ color: mov.tipo_lanc === 'C' ? '#2E7D32' : '#212121', fontSize: '14px' }">
                  {{ mov.tipo_lanc === 'C' ? '+' : '-' }} {{ formatarMoeda(mov.valor) }}
                </div>
                <div class="mt-1 font-weight-bold" style="color: #9E9E9E; font-size: 11px;">
                  Saldo: {{ formatarMoeda(mov.saldo) }}
                </div>
              </div>

            </div>
          </template>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ExtratoValeDialog',
  props: {
    // O v-model do componente pai se liga a esta prop 'value'
    value: {
      type: Boolean,
      default: false
    },
    cta: {
      type: [String, Number],
      required: true
    },
    nomeVale: {
      type: String,
      default: 'Extrato do Vale'
    }
  },
  data: () => ({
    carregando: false,
    movimentacoes: []
  }),
  computed: {
    // Computed property para ler/escrever o v-model (abrir/fechar dialog)
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    saldoAtual() {
      // Pega o saldo da primeira posição (a mais recente, segundo seu JSON)
      return this.movimentacoes.length > 0 ? this.movimentacoes[0].saldo : 0;
    }
  },
  watch: {
    // Sempre que o dialog for aberto, ele busca os dados
    dialogVisible(abriu) {
      if (abriu && this.cta) {
        this.buscarExtrato();
      }
    }
  },
  methods: {
    async buscarExtrato() {
      this.carregando = true;
      this.movimentacoes = []; // Limpa a lista anterior por segurança
      try {
        const res = await axios.get('/colaborador/movimentacao-cartoes-vales', {
          params: { cta: this.cta }
        });
        if (res.data && res.data.success) {
          this.movimentacoes = res.data.data;
        }
      } catch (error) {
        console.error("Erro ao buscar extrato do vale:", error);
      } finally {
        this.carregando = false;
      }
    },
    fecharDialog() {
      this.dialogVisible = false;
    },
    formatarMoeda(valor) {
      if (!valor && valor !== 0) return 'R$ 0,00';
      // Usa Math.abs para esconder o sinal de negativo grudado no valor, já que colocamos o sinal manualmente no HTML (+ ou -)
      return Math.abs(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    getDia(dataString) {
      if (!dataString) return '--';
      // Divide a data YYYY-MM-DD para evitar problemas de fuso horário no JS
      const partes = dataString.split('-');
      return partes[2] || '--';
    },
    getMes(dataString) {
      if (!dataString) return '--';
      const partes = dataString.split('-');
      if (partes.length < 2) return '--';
      
      const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
      const mesIndex = parseInt(partes[1], 10) - 1;
      return meses[mesIndex] || '--';
    }
  }
}
</script>